/* eslint-disable indent */
import { useNavigation } from '@react-navigation/native';

import { AppHeaderAction } from '@/components/AppHeader/AppHeaderAction';
import { useEffect, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';

import { Icon } from '@/components/Icon';
import colors from '@/config/colors';
import { logError } from '@/utils/logHandling';
import { fetchPatientsList } from '@/api/services/user';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';
import { updatePatientSettings } from '../../api/services/patient';
import { TherapistStackParamList } from '../../navigations/TherapistStack';
import store, { useStoreActions, useStoreState } from '../../stores/store';
import { ActiveSets } from './ActiveSets';
import AuditoryLanguageComprehension from './AuditoryLanguageComprehension';
import { ConnectPatient } from './ConnectPatient';
import { DailyGoal } from './DailyGoal';
import DesignateInWriting from './DesignateInWriting';
import OralNaming from './OralNaming';
import ReadingComprehension from './ReadingComprehension';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { Modal } from '@/components/ui/therapist/Modal';
import { PrimaryButton } from '@/components/Button';
import Card from '@/components/ui/therapist/Card';
import { replacedPatientProfileHeader } from '@/utils/stringHelpers';

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'column',
  },
  settingsCard: {
    width: '100%',
    backgroundColor: colors.white,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  textStyle: {
    fontWeight: '800',
  },

  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 8,
  },
  customContainer: {
    flexDirection: 'row',
    alignSelf: 'flex-end',
  },
});

function SelfTrainingSettings(
  props: StackScreenProps<TherapistStackParamList, 'SelfTrainingSettings'>,
) {
  const [code, setCode] = useState('');
  const [active, setAcitve] = useState('');
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [validCode, setValidCode] = useState(false);
  const saveSettings = useStoreActions((actions) => actions.saveSettings);
  const { showNotification } = useNotification();

  const patient = useStoreState((state) => state.patient);
  if (!patient) {
    throw new Error('Could find patient');
  }

  const navigation =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();

  const handlePressSave = async () => {
    try {
      activewords();
      saveSettings();
      const patient = store.getState().patient;
      if (!patient) {
        throw new Error();
      }
      const { exercises, ...payload } = patient;

      const validity = await updatePatientSettings(
        payload,
        props.route.params.id,
      );
      setValidCode(validity);
      setShowSaveConfirm(true);
    } catch (error) {
      showNotification({
        type: 'error',
        title: 'Einstellungen konnten nicht gespeichert werden.',
      });
      logError('Could not save self training settings', error);
    }
  };

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <View style={{ gap: 30, flexDirection: 'row' }}>
          <AppHeaderAction
            primary
            onPress={navigation.goBack}
            icon={<Icon name="close" scale={1.2} />}
            text="Abbrechen"
          />
          <AppHeaderAction
            primary
            onPress={handlePressSave}
            icon={<Icon name="save" />}
            text="Speichern"
          />
        </View>
      ),
    });
  }, []);

  const onPressSave = async () => {
    const hasInternet = await hasInternetAsync();
    if (!hasInternet) {
      props.navigation.goBack();
      return;
    }
    try {
      const patients = await fetchPatientsList();
      setShowSaveConfirm(false);
      props.navigation.navigate('PatientProfile', {
        id: patient._id,
        patients,
        replacedTitle: replacedPatientProfileHeader({
          patients: undefined,
          patientId: patient._id,
          patient,
        }),
      });
    } catch (error) {
      logError('Could not fetch patients after saving settings', error);
    }
  };

  const activewords = () => {
    let word = '';
    let sentence = '';
    let text = '';

    patient.wordsets.forEach((item) => {
      const removed = patient.activeWordsets.filter((data) => data == item._id);
      if (removed == item._id && patient.activeWordsets.length > 1) {
        word = word + item.name + ', ';
      } else if (removed == item._id) {
        word = word + item.name;
      }
    });

    patient.textsets.forEach((item) => {
      const removed = patient.activeTextsets.filter((data) => data == item._id);
      if (removed == item._id && patient.activeTextsets.length > 1) {
        text = text + item.name + ', ';
      } else if (removed == item._id) {
        text = text + item.name;
      }
    });

    patient.sentencesets.forEach((item) => {
      const removed = patient.activeSentencesets.filter(
        (data) => data == item._id,
      );
      if (removed == item._id && patient.activeSentencesets.length > 1) {
        sentence = sentence + item.name + ', ';
      } else if (removed == item._id) {
        sentence = sentence + item.name;
      }
    });

    word =
      word +
      (sentence.length > 0
        ? (word.length > 0 ? ', ' : '') + sentence
        : sentence) +
      (text.length > 0
        ? (sentence.length > 0 ? ', ' : word.length > 0 ? ', ' : '') + text
        : text);
    setAcitve(word.replace(' ,', ''));
  };
  const PatientPopupLine = () => (
    <View style={styles.container}>
      <Icon
        name={validCode ? 'checkCircle' : 'alertCircle'}
        size={24}
        color={validCode ? colors.primary : colors.red}
      />

      {validCode === true && (
        <Text style={{ fontSize: 20 }}>
          Patient:in{' '}
          <Text style={[{ fontSize: 20 }, styles.textStyle]}>
            {patient.firstName} {patient.lastName}{' '}
          </Text>
          ist mit Code{' '}
          <Text style={[{ fontSize: 20 }, styles.textStyle]}>
            {patient.code}{' '}
          </Text>
          verbunden
        </Text>
      )}

      {!validCode && code.trim().length > 0 && (
        <Text style={{ fontSize: 20 }}>
          Es wurde ein ungültiger Patienten-Code für Patient:in{' '}
          <Text style={[{ fontSize: 20 }, styles.textStyle]}>
            {patient.firstName} {patient.lastName}{' '}
          </Text>
          eingegeben
        </Text>
      )}

      {!validCode && !code.trim().length && (
        <Text style={{ fontSize: 20 }}>
          Patient:in{' '}
          <Text style={[{ fontSize: 20 }, styles.textStyle]}>
            {patient.firstName} {patient.lastName}{' '}
          </Text>
          ist mit keinem Patienten-Code verknüpft
        </Text>
      )}
    </View>
  );

  const PatientPopupLine2 = () => {
    const setsSelected =
      patient.activeTextsets.length > 0 ||
      patient.activeSentencesets.length > 0 ||
      patient.activeWordsets.length > 0;
    return (
      <View style={styles.container}>
        <Icon
          name={setsSelected ? 'checkCircle' : 'alertCircle'}
          size={24}
          color={setsSelected ? colors.primary : colors.red}
        />

        {setsSelected ? (
          <Text style={{ fontSize: 20 }}>
            Die aktiven Übungssets sind:{' '}
            <Text style={[{ fontSize: 20 }, styles.textStyle]}>{active}</Text>
          </Text>
        ) : (
          <Text style={{ fontSize: 20 }}>
            Keine aktiven Übungssets ausgewählt.
          </Text>
        )}
      </View>
    );
  };

  return (
    <>
      <ScrollView style={{ backgroundColor: colors.newUI.therapist.bgBlue10 }}>
        <View style={{ gap: 42, padding: 32 }}>
          <Card title="Aphasie-App zum Eigentraining verbinden">
            <ConnectPatient code={code} setCode={setCode} />
          </Card>
          <Card title="Übungssets für das Eigentraining aktivieren">
            <ActiveSets />
          </Card>

          <Card title="Tagesziel Übungsdauer">
            <DailyGoal />
          </Card>

          <Card title="Lesesinnverständnis" styleBody={{ padding: 0 }}>
            <ReadingComprehension />
          </Card>

          <Card title="Auditives Sprachverständnis" styleBody={{ padding: 0 }}>
            <AuditoryLanguageComprehension />
          </Card>

          <Card title="Schriftliches Benennen" styleBody={{ padding: 0 }}>
            <DesignateInWriting />
          </Card>

          <Card title="Mündliches Benennen" styleBody={{ padding: 0 }}>
            <OralNaming />
          </Card>
        </View>
      </ScrollView>
      <Modal
        onClose={() => {
          setShowSaveConfirm(false);
          props.navigation.goBack();
        }}
        isVisible={showSaveConfirm}
        confirmActionButton={
          <PrimaryButton label="Fertig" onPress={onPressSave} />
        }
        title="Einstellungen für das Eigentraining gespeichert."
      >
        <View>
          {PatientPopupLine()}
          {PatientPopupLine2()}
        </View>
      </Modal>
    </>
  );
}

export default SelfTrainingSettings;
